/**
 * Klienti — the Smile Parking Club member list.
 *
 * The legacy screen is `/club_clients`: 67,119 members over eighteen columns,
 * ten of which are the points ledger arranged in three groups (Získáno /
 * Použito / Zbývá) under a spanned header row. That arrangement is kept, group
 * for group and label for label, because it is the shape of the question staff
 * ask — "how many did they earn, how many did they spend, what is left" — and
 * a manager who has read it a hundred times should not have to relearn where
 * the number is.
 *
 * Three things differ from the old screen, all of them deliberate:
 *
 * 1. Four columns show real values. `class.ClubUser.php` selects literal zeros
 *    for Rezervací, Poslední odjezd, Poslední rezervace and Aff #, so the old
 *    page prints "0" and "1. 1. 1970" for every member it has ever shown. They
 *    are computed here from the same definitions the legacy code uses in its
 *    own getAffPartners() query.
 *
 * 2. The member's name, car and plate appear under the e-mail. The old screen
 *    joins only the preferred profile, which resolves for 501 members out of
 *    67,119 — so those columns are blank on 99.3% of rows. Here the API falls
 *    back to the newest usable profile, which fills 19,906 of them.
 *
 * 3. The points columns are not sortable, and the header says so rather than
 *    offering a control. They are aggregated per page from three side tables;
 *    a sort control over them would either reorder just the visible page and
 *    quietly lie about the rest, or group 102,303 point rows on every click.
 *
 * The row expands rather than navigating, exactly as on Uzávěrky. A member is
 * a ledger and a booking history, and both are read *against* the list — a
 * page of their own would cost the comparison you opened them for.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type {
  ClientDetail,
  ClientPage,
  ClientPoints,
  ClientProfile,
  ClientRow,
  ClientSort,
} from '@smile/shared';
import { CLIENT_SORTS, CLIENT_STATUS_FILTERS, CLUB_TIERS, MAX_PAGE } from '@smile/shared';
import { api, ApiError } from '../api/client';
import { ClubPointsLedger } from '../components/ClubPointsLedger';
import { clenWord, CZK, STATUS_CLASS, STATUS_LABEL, TIER_LABEL } from '../lib/format';
import {
  dayDt,
  isEmail,
  isPhone,
  longDt,
  oneOf,
  oneOfNum,
  pageList,
  pageParam,
  SEARCH_DEBOUNCE_MS,
  todayISO,
} from '../lib/filters';
import { Footer } from '../components/Footer';

/**
 * Rows per page, and the sizes offered.
 *
 * Rezervace has had this control and a page-number box since it was built.
 * This screen had neither, on a table that is 2,685 pages long at twenty-five
 * a page — so reaching page 900 meant clicking the arrow, or the last-page
 * button and then back, several hundred times. Two grids that look identical
 * and page differently is exactly the kind of difference staff are told about
 * once and rediscover for a year, so this is Rezervace's control, in
 * Rezervace's place, with Rezervace's wording.
 *
 * The ceiling is 100 rather than Rezervace's 200 because the server says so:
 * ClientQuery caps perPage at 100, and each listed page costs five batched
 * side queries plus the club columns. Offering 200 here would produce a 400
 * from the API and look like a broken screen.
 */
const PER_PAGE = 25;
const PER_PAGE_OPTIONS = [25, 50, 100] as const;

/** How many bookings the expanded row shows before deferring to the count. */
const HISTORY_SHOWN = 12;

/**
 * Membership state. These are the legacy screen's own three words — a member
 * is not "aktivní/neaktivní" here, that distinction is a separate axis
 * (firstLogin) which the toolbar exposes and the row shows as a chip.
 */
const STAV_LABEL: Record<ClientRow['status'], string> = {
  ok: 'Člen',
  disabled: 'Zablokován',
  deleted: 'Smazán',
};

const STAV_CLASS: Record<ClientRow['status'], string> = {
  ok: 'stav',
  disabled: 'stav warn',
  deleted: 'stav gray',
};

const num = (n: number) => CZK.format(Math.round(n));

/** 'YYYY-MM-DD HH:MM:SS' -> '24. 7. 2026'. Dates only; the time is noise here. */
function day(v: string | null): string {
  if (!v || v.length < 10) return '—';
  return `${Number(v.slice(8, 10))}. ${Number(v.slice(5, 7))}. ${v.slice(0, 4)}`;
}

/* ------------------------------------------------------------------ *
 * Cells
 * ------------------------------------------------------------------ */

/**
 * The member. E-mail is the identifier staff paste and search by, so it leads;
 * the name, car and plate sit under it in the same sub-line the Rezervace grid
 * uses for the same purpose.
 */
function Clen({ r }: { r: ClientRow }) {
  const id = r.identity;
  const line = id ? [id.driverName, id.car, id.spz].filter(Boolean).join(' · ') : '';
  return (
    <td className="clen">
      <span className="mail">{r.email || '—'}</span>
      {line && <div className="sub">{line}</div>}
      {!!r.internalNote && (
        <div className="sub pozn" title={r.internalNote}>
          ✎ {r.internalNote}
        </div>
      )}
    </td>
  );
}

/** A profile card in the expanded row. Deleted ones are shown and marked. */
function Profil({ p }: { p: ClientProfile }) {
  const cls = ['profil', p.preferred ? 'pref' : '', p.status === 'deleted' ? 'del' : '']
    .filter(Boolean)
    .join(' ');
  const invoice = [p.invoiceCompany, p.invoiceStreet, p.invoiceCity, p.invoicePsc]
    .filter(Boolean)
    .join(', ');
  const phone = [p.phonePrefix, p.driverPhone].filter(Boolean).join(' ');
  return (
    <div className={cls}>
      <div className="h">
        <b>{p.driverName || p.name || `Profil #${p.id}`}</b>
        {p.preferred && <span className="tag">preferovaný</span>}
        {p.status === 'deleted' && <span className="tag off">smazaný</span>}
      </div>
      {(p.car || p.spz) && (
        <div className="l">
          {p.car} {p.spz && <span className="spz">{p.spz}</span>}
        </div>
      )}
      {p.driverEmail && (
        <div className="l">
          {isEmail(p.driverEmail) ? (
            <a href={`mailto:${p.driverEmail}`}>{p.driverEmail}</a>
          ) : (
            p.driverEmail
          )}
        </div>
      )}
      {phone && (
        <div className="l">{isPhone(phone) ? <a href={`tel:${phone}`}>{phone}</a> : phone}</div>
      )}
      {invoice && <div className="l fak">{invoice}</div>}
      {(p.invoiceIc || p.invoiceDic) && (
        <div className="l fak">
          {p.invoiceIc && `IČ ${p.invoiceIc}`} {p.invoiceDic && `DIČ ${p.invoiceDic}`}
        </div>
      )}
    </div>
  );
}

/**
 * The expanded row: the ledger written out in words, every profile, and the
 * recent bookings. The ten point figures are already on the row above, so what
 * this adds is the arithmetic between them — which is the thing a member on
 * the phone is actually disputing.
 */
function Detail({ id, row }: { id: number; row: ClientRow }) {
  const { data, isPending, error } = useQuery<ClientDetail>({
    queryKey: ['client', id],
    queryFn: () => api.client(id),
  });

  const p = row.points;
  const today = todayISO();

  return (
    <tr className="detailrow">
      <td colSpan={18}>
        <div className="klientdetail">
          <div className="card ucet">
            <h3>Bodový účet</h3>
            <div className="row">
              <span className="lbl">Získáno za parkování</span>
              <span className="val">{num(p.earnedParking)}</span>
            </div>
            <div className="row">
              <span className="lbl">Získáno za služby</span>
              <span className="val">{num(p.earnedExtras)}</span>
            </div>
            <div className="row">
              <span className="lbl" title="Body získané za členy, které tento klient přivedl.">
                Získáno z doporučení
              </span>
              <span className="val">{num(p.earnedAffiliate)}</span>
            </div>
            <div className="row total">
              <span className="lbl">Získáno celkem</span>
              <span className="val">{num(p.earnedTotal)}</span>
            </div>
            <div className="row">
              <span className="lbl">Použito</span>
              {/* The sign is part of the number, so it only appears when
                  there is a number to sign. "Použito −0" is not a smaller
                  amount than "Použito 0", it is the same amount written as
                  though something had happened — and on this screen the
                  majority of members have never redeemed anything, so it was
                  the common case rather than the edge one. */}
              <span className="val">
                {p.spentTotal > 0 ? `−${num(p.spentTotal)}` : num(0)}
              </span>
            </div>
            <div className="row total">
              <span className="lbl">Zbývá</span>
              <span className="val">{num(p.remainingTotal)}</span>
            </div>
            <div className="basis">
              Body za doporučení se utrácejí stejně jako vlastní. „Zbývá“ proto
              odečítá čerpání od všech získaných bodů daného druhu, ne jen od
              vlastních — stejně jako ve starém systému.
            </div>
          </div>

          <div className="klientmeta">
            <div className="field">
              <dt>Registrace</dt>
              <dd>{row.registration ? longDt(row.registration) : 'neuvedena'}</dd>
            </div>
            <div className="field">
              <dt>První přihlášení</dt>
              <dd>{row.firstLogin ? longDt(row.firstLogin) : 'nikdy'}</dd>
            </div>
            <div className="field">
              <dt>Poslední rezervace</dt>
              <dd>{row.lastCreated ? longDt(row.lastCreated) : '—'}</dd>
            </div>
            <div className="field">
              <dt>Poslední odjezd</dt>
              <dd>{row.lastDeparture ? longDt(row.lastDeparture) : '—'}</dd>
            </div>
            <div className="field">
              {/* The label used to be "Přivedl členů" with a bare number
                  under it, which reads "Přivedl členů 1" — the genitive
                  plural is what a numeral governs, so the label was already
                  committing to a count it could not see. Moving the noun into
                  the value lets it agree: 1 člen, 2 členové, 5 členů. */}
              <dt>Přivedl</dt>
              <dd>
                {num(row.affiliates)} {clenWord(row.affiliates)}
              </dd>
            </div>
            <div className="field">
              <dt>Přiveden členem</dt>
              <dd>{row.parentId ? `#${row.parentId}` : '—'}</dd>
            </div>
            {!!row.internalNote && (
              <div className="field wide">
                <dt>Interní poznámka</dt>
                <dd className="note">{row.internalNote}</dd>
              </div>
            )}
          </div>

          {data && <ClubPointsLedger clubUserId={id} rows={data.points} capped={data.pointsCapped} />}

          <div className="klientprofily">
            <h3>
              Profily{' '}
              {data && <span className="cnt">{data.profiles.length}</span>}
            </h3>
            {isPending && <div className="skeleton" />}
            {error && <div className="state">Detail se nepodařilo načíst.</div>}
            {data?.profiles.length === 0 && (
              <div className="state">Tento člen nemá uložený žádný profil.</div>
            )}
            {data?.profiles.map((pr) => <Profil key={pr.id} p={pr} />)}
          </div>

          {/* The heading counts the rows underneath it, not the list column.

              It used to print `row.reservations`, which is the *counting*
              total from the list query — everything except noshow, canceled
              and deleted — while the table beneath it lists the member's
              bookings with no status filter at all. The two numbers are
              different by construction, and the screen showed them one above
              the other as though they were the same one. Staging member 67625
              has 26 bookings, all of them noshow: the heading read
              "Rezervace 0" and directly below it twelve rows appeared,
              followed by "Zobrazeno 12 z 26". 5,172 members have more history
              than they have counting bookings and would show that
              contradiction to some degree.

              The history stays unfiltered — an operator looking a member up is
              very often looking for the no-show — so the heading is what
              moved. The counting number has not disappeared: it is in the
              `Rezervací` column on the row itself, and the note below states
              it explicitly whenever it differs, which is the only time the
              difference needs explaining.

              The old empty state read "Žádná rezervace, která se počítá.",
              which describes the excluded case — the one message it could
              never be used for, since the list it guards is unfiltered and is
              empty only when the member has never booked anything. */}
          <div className="klienthist">
            <h3>
              Rezervace{' '}
              {data && (
                <span className="cnt">
                  {num(data.reservations.length)}
                  {data.reservationsCapped ? '+' : ''}
                </span>
              )}
            </h3>
            {isPending && <div className="skeleton" />}
            {error && <div className="state">Historii se nepodařilo načíst.</div>}
            {data && data.reservations.length === 0 && (
              <div className="state">Tento člen zatím nemá žádnou rezervaci.</div>
            )}
            {data && data.reservations.length > 0 && (
              <table className="mini">
                <tbody>
                  {data.reservations.slice(0, HISTORY_SHOWN).map((res) => (
                    <tr key={res.id}>
                      <td>#{res.id}</td>
                      <td>
                        <span className={STATUS_CLASS[res.status] ?? 'stav'}>
                          {STATUS_LABEL[res.status] ?? res.status}
                        </span>
                      </td>
                      <td>{dayDt(res.arrival, today)}</td>
                      <td>{dayDt(res.departure, today)}</td>
                      <td className="num">{num(res.price)} Kč</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {data && data.reservations.length > HISTORY_SHOWN && (
              <div className="more">
                Zobrazeno {HISTORY_SHOWN} z {num(data.reservations.length)}
                {data.reservationsCapped ? ' (historie je oříznuta)' : ''}.
              </div>
            )}
            {/* Only when the two numbers actually differ, which is 5,172 of
                67,119 members — on everybody else the sentence would be a
                restatement of the heading and would train staff to skip it.

                Guarded on `!reservationsCapped` because past the cap the
                heading is a lower bound while `row.reservations` is not, so
                "counts only N of them" could print an N larger than the
                number above it. The cap is well above the busiest member in
                staging (86 bookings), so this is a correctness guard rather
                than a case anyone is expected to meet.

                The verb is avoided deliberately: Czech agreement after a
                numeral would need "je 1 / jsou 2 / je 5", and `počítá … z
                nich` reads correctly for every count including zero. */}
            {data &&
              !data.reservationsCapped &&
              data.reservations.length !== row.reservations && (
                <div className="basis">
                  Sloupec <em>Rezervací</em> počítá jen{' '}
                  {num(row.reservations)} z nich — zrušené, nedojeté a smazané
                  rezervace se do něj nezapočítávají.
                </div>
              )}
          </div>
        </div>
      </td>
    </tr>
  );
}

/* ------------------------------------------------------------------ *
 * Screen
 * ------------------------------------------------------------------ */

const ZERO: ClientPoints = {
  earnedTotal: 0,
  earnedParking: 0,
  earnedExtras: 0,
  earnedAffiliate: 0,
  spentTotal: 0,
  spentParking: 0,
  spentExtras: 0,
  remainingTotal: 0,
  remainingParking: 0,
  remainingExtras: 0,
};

export function Klienti() {
  const [sp, setSp] = useSearchParams();

  /* Every one of these is read out of the address bar, so none of them is
     trusted. `razeni` used to be cast straight to the union and handed to the
     API: `?razeni=nazev` — a typo, a truncated paste, an old bookmark from a
     column that was renamed — reached the server, came back 400, and left the
     screen showing an error card that no control on the page could clear.
     "Zrušit filtry" does not help, because sorting is not a filter and the
     button does not touch it. The operator's only route out was to know that
     the URL was the problem.

     `stav` and `uroven` had the same exposure through a different door: they
     were passed through untouched, so a value the server's enum rejects
     produced the same permanent 400. The lists come from shared/rules.ts —
     the same tuples the Zod schemas are built from — so what the browser
     accepts and what the API accepts cannot drift apart. */
  const q = sp.get('hledat') ?? '';
  const status = oneOf(sp.get('stav'), CLIENT_STATUS_FILTERS, '' as const);
  const tier = oneOf(sp.get('uroven'), CLUB_TIERS, '' as const);
  const sort = oneOf(sp.get('razeni'), CLIENT_SORTS, 'email');
  const dir = sp.get('smer') === 'desc' ? 'desc' : 'asc';
  /* `strana` sat in this same list, read raw, while the paragraph above
     described the exact bug it had. `Math.max(1, Number(...) || 1)` is right
     about 'abc' and about '-3' and wrong about '2.5' and '1e9', both of which
     the API's schema refuses. See pageParam. */
  const page = pageParam(sp.get('strana'), MAX_PAGE);
  /* Read the same way as `stav` and `uroven`: anything not on the list becomes
     the default rather than being forwarded. The reasoning, and what a
     forwarded value would actually do to the screen, is on `oneOfNum`. */
  const perPage = oneOfNum(sp.get('radku'), PER_PAGE_OPTIONS, PER_PAGE);

  const [open, setOpen] = useState<number | null>(null);

  const apply = (patch: Record<string, string | number | null>) => {
    const next = new URLSearchParams(sp);
    for (const [k, v] of Object.entries(patch)) {
      if (v == null || v === '') next.delete(k);
      else next.set(k, String(v));
    }
    // Same rule as everywhere else: any change but a page change invalidates
    // the page number, because staying on page 7 of a two-page filter shows an
    // empty table and reads as a broken screen.
    if (!('strana' in patch)) next.delete('strana');
    setSp(next, { replace: true });
  };

  /* ---- free-text box, debounced against the URL — the Rezervace pattern ----
     This screen used to be the odd one out: it searched on Enter, and also on
     blur, while Rezervace and Statistiky searched as you type. Three screens
     and two behaviours for a box that looks identical in all three is the kind
     of difference nobody can be told about once; it is discovered, repeatedly,
     as "the search is broken here". Blur was the worse half — click a filter
     after typing and the results changed underneath the click.

     `settled` is what keeps the two directions from fighting. The URL drives
     the box (so Back, a pasted link and "Zrušit filtry" all put it back), and
     the box drives the URL after a pause; without a record of which value the
     two last agreed on, each update looks to the other like something to
     follow, and a keystroke arriving mid-flight resets the box to the old
     term. */
  const [text, setText] = useState(q);
  const settled = useRef(q);

  useEffect(() => {
    // The URL changed behind our back — Back/forward, a pasted link, or the
    // reset button. Follow it.
    if (q !== settled.current) {
      settled.current = q;
      setText(q);
    }
  }, [q]);

  useEffect(() => {
    const term = text.trim();
    if (term === settled.current) return;
    const id = setTimeout(() => {
      settled.current = term;
      apply({ hledat: term });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  const { data, isPending, isFetching, error, refetch } = useQuery<ClientPage>({
    queryKey: ['clients', q, status, tier, sort, dir, page, perPage],
    queryFn: () => api.clients({ q, status, tier, sort, dir, page, perPage }),
    // Paging a 2,685-page table one arrow at a time should not blank the
    // screen between clicks. The previous page stays on screen and the
    // heading says "aktualizuji" — same behaviour as Rezervace.
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    setOpen(null);
  }, [q, status, tier, sort, dir, page, perPage]);

  const totalPages = useMemo(
    () => (data ? Math.max(1, Math.ceil(data.total / data.perPage)) : 1),
    [data],
  );

  /* Clamped here rather than trusted, because this number is typed by hand.
     `Strana 0` and `Strana 4000` both used to be reachable, and both showed an
     empty grid that reads as a broken screen rather than as a typo. */
  const goToPage = (n: number) =>
    apply({ strana: Math.min(totalPages, Math.max(1, Math.floor(n) || 1)) });

  const sortBy = (col: ClientSort) =>
    apply({ razeni: col, smer: sort === col && dir === 'asc' ? 'desc' : 'asc' });

  const sortProps = (col: ClientSort, extra = '') => ({
    className: `${extra} sortable`.trim(),
    role: 'button' as const,
    tabIndex: 0,
    'aria-sort': (sort === col ? (dir === 'asc' ? 'ascending' : 'descending') : 'none') as
      | 'ascending'
      | 'descending'
      | 'none',
    onClick: () => sortBy(col),
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        sortBy(col);
      }
    },
  });

  const arrow = (col: ClientSort) =>
    sort === col ? (
      <span className="arrow" aria-hidden="true">
        {dir === 'asc' ? '▴' : '▾'}
      </span>
    ) : null;

  /* The one screen in the application that most staff cannot open. Rather than
     the generic "nepodařilo se načíst", which reads as a fault and generates a
     support call, a 403 says plainly whose screen this is. */
  if (error instanceof ApiError && error.status === 403) {
    return (
      <div className="container">
        <div className="pagehead">
          <h1>Klienti</h1>
        </div>
        <div className="card state">
          Seznam členů Smile Parking Club je přístupný jen manažerům a
          administrátorům — obsahuje kontaktní a fakturační údaje zákazníků.
          Zákazníka podle jména, e-mailu, telefonu nebo SPZ najdete
          v Rezervacích.
        </div>
      </div>
    );
  }

  const totals = data?.totals ?? ZERO;

  return (
    <div className="container">
      <div className="pagehead">
        <h1>Smile Parking členové</h1>
        <span className="sub recordcount">
          {data ? `${CZK.format(data.total)} ${clenWord(data.total)}` : '…'}
          {isFetching && !isPending ? ' · aktualizuji' : ''}
        </span>
      </div>

      <div className="card tablecard">
        <div className="toolbar">
          <input
            className="search"
            type="search"
            placeholder="E-mail, jméno, SPZ, ID…"
            value={text}
            onChange={(e) => setText(e.target.value)}
            /* Enter and Escape stay, and both jump the queue rather than
               waiting out the pause: somebody who presses Enter has finished
               typing and should not then wait, and somebody who presses
               Escape wants the list back now. Setting `settled` in both
               places is what stops the pending timer from re-applying the
               term a moment later. */
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                settled.current = text.trim();
                apply({ hledat: text.trim() });
              }
              if (e.key === 'Escape') {
                settled.current = '';
                setText('');
                apply({ hledat: null });
              }
            }}
            aria-label="Hledat v členech"
          />
          {/* Two different questions used to sit in one flat list, and the
              second one had been given a new name. `pending` read "Nikdy
              nepřihlášen" here while the chip on the matching rows read
              "neaktivní" — the same fact, two words, and the word on the rows
              is the legacy one. Somebody filtering for members who had never
              signed in had no way to know those were the same thing.

              The legacy screen does not have this problem, because it does not
              flatten the two: the three stored states are the list, and the
              login question is an optgroup underneath headed "Přihlásil se
              někdy?" answered Ano / Ne. That is what is below. The group
              heading carries the meaning, so the options do not have to
              re-name a state that already has a name on screen.

              The state labels stay Člen / Zablokován / Smazán rather than the
              legacy filter's Ok / Pozastaven / Smazaný: the legacy filter and
              the legacy rows disagree with each other on those three, and of
              the two vocabularies the one on the rows is the one staff read
              all day. */}
          <select value={status} onChange={(e) => apply({ stav: e.target.value })} aria-label="Stav">
            <option value="">Všechny stavy</option>
            <option value="ok">Člen</option>
            <option value="disabled">Zablokován</option>
            <option value="deleted">Smazán</option>
            <optgroup label="Přihlásil se někdy?">
              <option value="active">Ano</option>
              <option value="pending">Ne</option>
            </optgroup>
          </select>
          <select value={tier} onChange={(e) => apply({ uroven: e.target.value })} aria-label="Úroveň">
            <option value="">Všechny úrovně</option>
            <option value="silver">Silver</option>
            <option value="gold">Gold</option>
            <option value="platinum">Platinum</option>
          </select>
          <span className="sep" />
          {(q || status || tier) && (
            <button
              className="btn"
              type="button"
              onClick={() => apply({ hledat: null, stav: null, uroven: null })}
            >
              Zrušit filtry
            </button>
          )}
        </div>

        <div className="tablewrap">
          <table className="klienttbl">
            {/* The grouped header is the legacy one, group for group. The blank
                span over the identity columns is what makes the three ledger
                groups readable as groups rather than as ten loose numbers. */}
            <thead>
              <tr className="grp">
                <th colSpan={8} />
                <th colSpan={4}>Získáno</th>
                <th colSpan={3}>Použito</th>
                <th colSpan={3}>Zbývá</th>
              </tr>
              <tr>
                <th {...sortProps('id')}>ID {arrow('id')}</th>
                <th {...sortProps('email')}>Člen {arrow('email')}</th>
                <th {...sortProps('status')}>Stav {arrow('status')}</th>
                <th {...sortProps('level')}>Úroveň {arrow('level')}</th>
                <th {...sortProps('registration')}>Registrace {arrow('registration')}</th>
                <th className="num">Rezervací</th>
                {/* Named, because "Poslední" alone sat two columns from
                    "Poslední rezervace" in the detail card and the two are
                    different dates. This is the real one — when the car left,
                    not when the booking said it would. */}
                <th title="Poslední skutečný odjezd. Členové, jejichž pobyt zatím neskončil, mají pomlčku.">
                  Poslední odjezd
                </th>
                <th className="num" title="Kolik členů tento klient přivedl.">
                  Aff #
                </th>
                {/* Deliberately not sortable — see the file header. The title
                    is the explanation a manager gets when they try. */}
                <th className="num pts" title="Bodové sloupce se nedají řadit: sčítají se pro zobrazenou stranu.">
                  Celkem
                </th>
                <th className="num pts">Park.</th>
                <th className="num pts">Služby</th>
                <th className="num pts">Dopor.</th>
                <th className="num pts">Celkem</th>
                <th className="num pts">Park.</th>
                <th className="num pts">Služby</th>
                <th className="num pts">Celkem</th>
                <th className="num pts">Park.</th>
                <th className="num pts">Služby</th>
              </tr>
            </thead>
            <tbody>
              {/* Three things Statistiky's failure card has and this one did
                  not. role="alert" so a screen reader is told rather than left
                  to notice. The server's own words, so a support call has a
                  sentence to quote instead of "it says it failed". And a
                  button — without one the only way out is reloading the page,
                  which throws away the filter and the page number the user
                  spent the last minute setting. */}
              {error && (
                <tr>
                  <td colSpan={18} className="state" role="alert">
                    Členy se nepodařilo načíst.
                    {error instanceof Error && error.message ? ` (${error.message})` : ''}{' '}
                    <button className="btn sm" type="button" onClick={() => refetch()}>
                      Zkusit znovu
                    </button>
                  </td>
                </tr>
              )}
              {isPending &&
                !error &&
                Array.from({ length: 10 }, (_, i) => (
                  <tr key={`k${i}`}>
                    <td colSpan={18}>
                      <div className="skeleton" />
                    </td>
                  </tr>
                ))}
              {data?.rows.length === 0 && (
                <tr>
                  <td colSpan={18} className="state">
                    Žádný člen neodpovídá zadání.
                  </td>
                </tr>
              )}
              {data?.rows.map((r) => {
                const isOpen = open === r.id;
                const cls = ['click', r.status === 'deleted' ? 'deleted' : '', isOpen ? 'on' : '']
                  .filter(Boolean)
                  .join(' ');
                return [
                  <tr
                    key={r.id}
                    className={cls}
                    /* Rezervace's rows have been focusable and Enter-operable
                       since they were built; these were mouse-only, and they
                       are the rows behind which a customer's telephone number
                       and address sit. A row that says aria-expanded and
                       cannot be reached by Tab is worse than one that says
                       nothing: it announces a control that is not there.

                       Space as well as Enter, because this row expands rather
                       than navigates, and Space is what a person expects from
                       a thing that opens in place. Both are prevented from
                       their defaults — Space would otherwise scroll the page
                       out from under the row it just opened. */
                    tabIndex={0}
                    role="button"
                    aria-expanded={isOpen}
                    aria-label={`${r.email || `Člen ${r.id}`} — ${isOpen ? 'skrýt' : 'zobrazit'} detail`}
                    onClick={() => setOpen(isOpen ? null : r.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setOpen(isOpen ? null : r.id);
                      }
                    }}
                  >
                    <td className="idcell">{r.id}</td>
                    <Clen r={r} />
                    <td>
                      <span className={STAV_CLASS[r.status]}>{STAV_LABEL[r.status]}</span>
                      {/* An account opened at the desk that the customer has
                          never signed in to. 41,488 of 67,119 members are in
                          this state, so it is a fact about the row, not an
                          alarm — hence a quiet chip and not a warning. */}
                      {r.status === 'ok' && !r.firstLogin && (
                        <div className="sub">
                          <span className="chip off">neaktivní</span>
                        </div>
                      )}
                    </td>
                    <td>
                      <span className={`uroven ${r.tier}`}>{TIER_LABEL[r.tier]}</span>
                    </td>
                    <td>{day(r.registration)}</td>
                    <td className="num">{num(r.reservations)}</td>
                    <td>{day(r.lastDeparture)}</td>
                    <td className="num">{r.affiliates ? num(r.affiliates) : '—'}</td>
                    <td className="num pts strong">{num(r.points.earnedTotal)}</td>
                    <td className="num pts">{num(r.points.earnedParking)}</td>
                    <td className="num pts">{num(r.points.earnedExtras)}</td>
                    <td className="num pts">{num(r.points.earnedAffiliate)}</td>
                    <td className="num pts strong">{num(r.points.spentTotal)}</td>
                    <td className="num pts">{num(r.points.spentParking)}</td>
                    <td className="num pts">{num(r.points.spentExtras)}</td>
                    <td className="num pts strong">{num(r.points.remainingTotal)}</td>
                    <td className="num pts">{num(r.points.remainingParking)}</td>
                    <td className="num pts">{num(r.points.remainingExtras)}</td>
                  </tr>,
                  isOpen ? <Detail key={`d${r.id}`} id={r.id} row={r} /> : null,
                ];
              })}
            </tbody>
            {/* Page totals, and the caption says page. Summing the whole filter
                would mean a second pass over 102,303 point rows for a figure
                nobody reconciles against anything. */}
            <tfoot>
              <tr className="pagetotal">
                <td colSpan={8}>Součet zobrazené strany</td>
                <td className="num pts">{num(totals.earnedTotal)}</td>
                <td className="num pts">{num(totals.earnedParking)}</td>
                <td className="num pts">{num(totals.earnedExtras)}</td>
                <td className="num pts">{num(totals.earnedAffiliate)}</td>
                <td className="num pts">{num(totals.spentTotal)}</td>
                <td className="num pts">{num(totals.spentParking)}</td>
                <td className="num pts">{num(totals.spentExtras)}</td>
                <td className="num pts">{num(totals.remainingTotal)}</td>
                <td className="num pts">{num(totals.remainingParking)}</td>
                <td className="num pts">{num(totals.remainingExtras)}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        <div className="pager">
          <span>
            {data
              ? `Strana ${data.page} z ${CZK.format(totalPages)} · ${CZK.format(data.total)} ${clenWord(data.total)}`
              : '…'}
          </span>

          <select
            value={perPage}
            onChange={(e) => apply({ radku: Number(e.target.value) })}
            style={{ marginLeft: 12 }}
            aria-label="Počet řádků na stranu"
          >
            {PER_PAGE_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n} / strana
              </option>
            ))}
          </select>

          {/* Same threshold as Rezervace: below ten pages the numbered buttons
              are already the fastest way there, and a spare box is clutter. */}
          {totalPages > 10 && (
            <label className="jump">
              Strana
              <input
                type="number"
                min={1}
                max={totalPages}
                /* Keyed on the page so that arriving somewhere by any other
                   route — a numbered button, Back, a pasted link — resets what
                   the box shows. Without the key it keeps whatever was last
                   typed into it and quietly disagrees with the table. */
                key={page}
                defaultValue={page}
                aria-label="Přejít na stranu"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    goToPage(Number((e.target as HTMLInputElement).value));
                  }
                }}
                onBlur={(e) => {
                  const n = Number(e.target.value);
                  if (n && n !== page) goToPage(n);
                }}
              />
            </label>
          )}

          <div className="pages">
            <button
              onClick={() => apply({ strana: Math.max(1, page - 1) })}
              disabled={page <= 1}
              aria-label="Předchozí strana"
            >
              ‹
            </button>
            {pageList(page, totalPages).map((p, i) =>
              p === '…' ? (
                <span key={`gap${i}`} className="gap">
                  …
                </span>
              ) : (
                <button
                  key={p}
                  className={p === page ? 'on' : undefined}
                  aria-current={p === page ? 'page' : undefined}
                  onClick={() => apply({ strana: p })}
                >
                  {p}
                </button>
              ),
            )}
            <button
              onClick={() => apply({ strana: Math.min(totalPages, page + 1) })}
              disabled={page >= totalPages}
              aria-label="Další strana"
            >
              ›
            </button>
          </div>
        </div>
      </div>

      {/* The same footer as every other screen. This was the one screen without
          it, and it is the screen that prints customers' names, telephone
          numbers and billing addresses — so it is the one where "am I looking
          at staging or at the real thing" is worth answering on the page. */}
      <Footer />
    </div>
  );
}
